/**
 * StepEditor (Phase 2.7)
 *
 * Renders the ordered step list inside a workflow plan authoring form
 * (TreatmentPlansPage create / edit). Each step row carries:
 *   - procedure picker (select from clinic catalog)
 *   - mandatory / optional toggle
 *   - default executor (TeamAssignmentPicker, role=NURSE|DOCTOR)
 *   - requires-approval flag + default approver picker (role=DOCTOR)
 *   - up/down reorder controls + remove
 *
 * The drag-and-drop nicety is deferred — up/down arrows are sufficient
 * for v1 (≤20 steps per plan) and don't require a third-party DnD lib.
 *
 * Caps from the backend: 20 steps/plan. The "+ Add step" button is
 * disabled at the cap.
 *
 * Follows CLAUDE.md day-mode contrast + button visibility rules: every
 * button has a solid bg-white / dark:bg-slate-800, borders are at least
 * border-slate-300 weight, and disabled uses opacity-60.
 */
import React from 'react';

import TeamAssignmentPicker from './TeamAssignmentPicker';


const MAX_STEPS = 20;


function emptyStep() {
  return {
    // Local-only id so React keys stay stable; real step_id is assigned
    // by the backend on create.
    _key: `tmp_${Math.random().toString(36).slice(2, 10)}`,
    procedure_id: '',
    is_mandatory: true,
    estimated_cost_override: '',
    discount_amount: '',
    discount_reason: '',
    tooth_numbers: '',
    default_executor_member_id: '',
    requires_approval: false,
    default_approver_member_id: '',
  };
}


export default function StepEditor({
  steps,
  onChange,
  procedures = [],   // [{procedure_id, name, default_cost}, ...] — clinic catalog
  doctorMemberId,
  doctorName,
  staff = [],
  // Phase 2.7 fix — the owner doctor's rate-override map keyed by
  // procedure_id, used to render the effective rate per step
  // (override_cost → catalog default_cost priority). Read-only here.
  rates = {},
  disabled = false,
}) {
  // Catalog lookup so we can resolve default_cost without an O(n) scan
  // inside each row.
  const procedureById = React.useMemo(() => {
    const map = {};
    for (const p of procedures) map[p.procedure_id] = p;
    return map;
  }, [procedures]);

  const updateAt = (idx, patch) => {
    const next = steps.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const removeAt = (idx) => {
    const next = steps.slice();
    next.splice(idx, 1);
    onChange(next);
  };
  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = steps.slice();
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  };
  const moveDown = (idx) => {
    if (idx === steps.length - 1) return;
    const next = steps.slice();
    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
    onChange(next);
  };
  const addStep = () => {
    if (steps.length >= MAX_STEPS) return;
    onChange([...steps, emptyStep()]);
  };

  return (
    <div className="space-y-3">
      {steps.length === 0 && (
        <div className="text-sm text-slate-600 dark:text-slate-300 italic py-4 text-center border border-dashed border-slate-300 dark:border-slate-600 rounded-md">
          No steps yet. Add a step to start designing this plan.
        </div>
      )}

      {steps.map((step, idx) => (
        <div
          key={step._key || step.step_id || idx}
          className="p-3 rounded-md border-2 border-indigo-400 dark:border-indigo-600 bg-indigo-50 dark:bg-slate-800 space-y-3"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs font-bold text-indigo-900 dark:text-indigo-100">
              Step {idx + 1}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={disabled || idx === 0}
                onClick={() => moveUp(idx)}
                className="text-xs font-medium px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
                aria-label="Move up"
              >
                ↑
              </button>
              <button
                type="button"
                disabled={disabled || idx === steps.length - 1}
                onClick={() => moveDown(idx)}
                className="text-xs font-medium px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
                aria-label="Move down"
              >
                ↓
              </button>
              <button
                type="button"
                disabled={disabled}
                onClick={() => removeAt(idx)}
                className="text-xs font-medium px-2 py-1 rounded-md border border-red-400 dark:border-red-700 bg-white dark:bg-slate-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-60"
              >
                Remove
              </button>
            </div>
          </div>

          {/* Row 1: procedure + mandatory toggle */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="sm:col-span-2 block">
              <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                Procedure
              </span>
              <select
                value={step.procedure_id || ''}
                disabled={disabled}
                onChange={(e) => updateAt(idx, { procedure_id: e.target.value })}
                className="w-full text-sm font-medium px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 disabled:opacity-60"
              >
                <option value="">Pick a procedure…</option>
                {procedures.map((p) => (
                  <option key={p.procedure_id} value={p.procedure_id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {/* Phase 2.7 fix — show the effective rate alongside the
                  picker. Doctor override wins; otherwise catalog default.
                  Read-only here; doctors edit overrides on /org/doctor-rates. */}
              {step.procedure_id && (() => {
                const proc = procedureById[step.procedure_id];
                const override = rates[step.procedure_id]?.override_cost;
                const hasOverride = override != null && override !== '';
                const effective = hasOverride
                  ? override
                  : proc?.default_cost;
                if (effective == null || effective === '') return null;
                return (
                  <div className="mt-1 flex items-center gap-1.5 text-xs">
                    <span className="font-semibold text-slate-700 dark:text-slate-200">
                      Rate ₹{effective}
                    </span>
                    <span
                      className={
                        'px-1.5 py-0.5 rounded font-semibold '
                        + (hasOverride
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                          : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200')
                      }
                    >
                      {hasOverride ? 'doctor override' : 'clinic default'}
                    </span>
                  </div>
                );
              })()}
            </label>
            <label className="flex items-end gap-2 pb-1">
              <input
                type="checkbox"
                checked={!!step.is_mandatory}
                disabled={disabled}
                onChange={(e) => updateAt(idx, { is_mandatory: e.target.checked })}
                className="h-4 w-4"
              />
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                Mandatory
              </span>
            </label>
          </div>

          {/* Row 2: cost override + discount + tooth numbers */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="block">
              <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                Cost override (₹)
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={step.estimated_cost_override ?? ''}
                disabled={disabled}
                onChange={(e) => updateAt(idx, { estimated_cost_override: e.target.value })}
                className="w-full text-sm font-medium px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 disabled:opacity-60"
                placeholder="(use catalog rate)"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                Discount (₹)
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={step.discount_amount ?? ''}
                disabled={disabled}
                onChange={(e) => updateAt(idx, { discount_amount: e.target.value })}
                className="w-full text-sm font-medium px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 disabled:opacity-60"
                placeholder="0"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                Tooth #s (comma-sep)
              </span>
              <input
                type="text"
                value={step.tooth_numbers ?? ''}
                disabled={disabled}
                onChange={(e) => updateAt(idx, { tooth_numbers: e.target.value })}
                className="w-full text-sm font-medium px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 disabled:opacity-60"
                placeholder="e.g. 11, 12"
              />
            </label>
          </div>

          {Number(step.discount_amount) > 0 && (
            <label className="block">
              <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                Discount reason (required when discount &gt; 0)
              </span>
              <input
                type="text"
                value={step.discount_reason ?? ''}
                disabled={disabled}
                onChange={(e) => updateAt(idx, { discount_reason: e.target.value })}
                className="w-full text-sm font-medium px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 disabled:opacity-60"
                placeholder="Min 3 chars — e.g. 'senior citizen courtesy'"
              />
            </label>
          )}

          {/* Row 3: assignee defaults */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                Default executor
              </span>
              <TeamAssignmentPicker
                doctorMemberId={doctorMemberId}
                doctorName={doctorName}
                value={step.default_executor_member_id}
                onChange={(v) => updateAt(idx, { default_executor_member_id: v })}
                staff={staff}
                disabled={disabled}
              />
            </label>
            <div className="block">
              <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                Approval
              </span>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={!!step.requires_approval}
                    disabled={disabled}
                    onChange={(e) => updateAt(idx, { requires_approval: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                    Requires approval
                  </span>
                </label>
                {step.requires_approval && (
                  <div className="flex-1 min-w-0">
                    <TeamAssignmentPicker
                      doctorMemberId={doctorMemberId}
                      doctorName={doctorName}
                      value={step.default_approver_member_id}
                      onChange={(v) => updateAt(idx, { default_approver_member_id: v })}
                      staff={staff}
                      roleFilter="DOCTOR"
                      disabled={disabled}
                      placeholder="Approver…"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        disabled={disabled || steps.length >= MAX_STEPS}
        onClick={addStep}
        className="w-full text-sm font-semibold px-3 py-2 rounded-md border border-indigo-400 dark:border-indigo-500 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/40 disabled:opacity-60"
      >
        + Add step
        {steps.length >= MAX_STEPS ? ' (max 20 reached)' : ''}
      </button>
    </div>
  );
}

export { emptyStep };
